import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiCheck, FiUsers, FiAlertTriangle, FiUser } = FiIcons;

const Assessment = () => {
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [currentTeam, setCurrentTeam] = useState(null);
  const [isTeamValid, setIsTeamValid] = useState(false);
  const [isGuestMode, setIsGuestMode] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (isAuthenticated) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      // Authenticated user flow
      const teamData = localStorage.getItem('currentTeam');
      if (teamData) {
        const team = JSON.parse(teamData);
        setCurrentTeam(team);
        
        // Validate that this team belongs to the current user
        const allTeams = JSON.parse(localStorage.getItem('teams') || '[]');
        const userTeam = allTeams.find(t => t.id === team.id && t.ownerId === userData.id);
        if (userTeam) {
          setIsTeamValid(true);
        } else {
          console.warn('Team not found or access denied, allowing assessment without team association');
          setIsTeamValid(true);
        }
      } else {
        setIsTeamValid(true);
      }

      // Load any existing assessment progress
      const savedAssessment = localStorage.getItem('currentAssessment');
      if (savedAssessment) {
        try {
          const saved = JSON.parse(savedAssessment);
          if (saved.userId === userData.id) {
            setResponses(saved.responses || {});
            setCurrentSection(saved.currentSection || 0);
          }
        } catch (error) {
          console.error('Error loading saved assessment:', error);
        }
      }
    } else {
      // Guest mode flow
      setIsGuestMode(true);
      setIsTeamValid(true);
      
      // Load any existing guest assessment progress
      const savedGuestAssessment = localStorage.getItem('guestAssessment');
      if (savedGuestAssessment) {
        try {
          const saved = JSON.parse(savedGuestAssessment);
          // Check if assessment is less than 24 hours old
          const assessmentAge = Date.now() - saved.timestamp;
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours
          
          if (assessmentAge < maxAge) {
            setResponses(saved.responses || {});
            setCurrentSection(saved.currentSection || 0);
          } else {
            // Clear old guest assessment
            localStorage.removeItem('guestAssessment');
          }
        } catch (error) {
          console.error('Error loading saved guest assessment:', error);
          localStorage.removeItem('guestAssessment');
        }
      }
    }
  }, [navigate]);

  const sections = [
    {
      title: 'Psychological Safety',
      description: 'How safe do team members feel to express themselves?',
      questions: [
        'If you make a mistake on this team, it is often held against you.',
        'Members of this team are able to bring up problems and tough issues.',
        'People on this team sometimes reject others for being different.',
        'It is safe to take a risk on this team.',
        'It is difficult to ask other members of this team for help.',
        'No one on this team would deliberately act in a way that undermines my efforts.',
        'Working with members of this team, my unique skills and talents are valued and utilized.'
      ]
    },
    {
      title: 'Dependability',
      description: 'Can team members rely on each other to do high quality work on time?',
      questions: [
        'When someone on this team says they\'ll do something, they follow through.',
        'Team members often fail to complete their assigned tasks on time.',
        'I can depend on my teammates to help me when I need it.',
        'The quality of work from team members is consistently high.',
        'Team members communicate proactively about delays or issues.'
      ]
    },
    {
      title: 'Structure & Clarity',
      description: 'Are goals, roles, and execution plans clear?',
      questions: [
        'The goals and objectives for our team are clear.',
        'I understand what is expected of me at work.',
        'The processes and procedures for our team are clear.',
        'I know what my teammates are working on.',
        'Our team has clear decision-making processes.'
      ]
    },
    {
      title: 'Meaning',
      description: 'Is the work personally meaningful to team members?',
      questions: [
        'The work I do on this team is meaningful to me.',
        'I feel a sense of personal accomplishment from the work I do.',
        'My work makes a difference to the organization.',
        'I am passionate about the mission of our team.',
        'The work we do aligns with my personal values.'
      ]
    },
    {
      title: 'Impact',
      description: 'Do team members believe their work matters?',
      questions: [
        'I can see how my work contributes to the organization\'s objectives.',
        'The work our team does makes a real difference.',
        'Our team\'s work is valued by the organization.',
        'I understand how my individual work fits into the bigger picture.',
        'The results of our team\'s work are visible and recognized.'
      ]
    }
  ];

  const scaleLabels = [
    'Strongly Disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly Agree'
  ];

  // Auto-save progress
  useEffect(() => {
    const saveProgress = () => {
      if (isGuestMode) {
        const guestAssessment = {
          responses,
          currentSection,
          timestamp: Date.now()
        };
        localStorage.setItem('guestAssessment', JSON.stringify(guestAssessment));
      } else {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const currentAssessment = {
          userId: userData.id,
          responses,
          currentSection,
          timestamp: Date.now()
        };
        localStorage.setItem('currentAssessment', JSON.stringify(currentAssessment));
      }
    };

    // Save progress with debounce
    const timeoutId = setTimeout(saveProgress, 1000);
    return () => clearTimeout(timeoutId);
  }, [responses, currentSection, isGuestMode]);

  const handleResponse = (questionIndex, value) => {
    const key = `${currentSection}-${questionIndex}`;
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    } else {
      // Calculate results and navigate
      const assessmentData = {
        responses,
        teamId: currentTeam?.id,
        teamName: currentTeam?.name,
        ownerId: currentTeam?.ownerId,
        completedAt: new Date().toISOString(),
        isGuestMode
      };
      localStorage.setItem('assessmentResults', JSON.stringify(assessmentData));
      navigate('/guest-results');
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const isCurrentSectionComplete = () => {
    const currentQuestions = sections[currentSection].questions;
    return currentQuestions.every((_, index) => {
      const key = `${currentSection}-${index}`;
      return responses[key] !== undefined;
    });
  };

  const progress = ((currentSection + 1) / sections.length) * 100;

  const handleStartOver = () => {
    if (window.confirm('Are you sure you want to start over? All current progress will be lost.')) {
      setResponses({});
      setCurrentSection(0);
      if (isGuestMode) {
        localStorage.removeItem('guestAssessment');
      } else {
        localStorage.removeItem('currentAssessment');
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Guest Mode Banner */}
      {isGuestMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiUser} className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                You are taking this assessment as a guest
              </span>
            </div>
            <Link
              to="/login"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Sign in
            </Link>
          </div>
        </div>
      )}

      {/* Team Info */}
      {currentTeam && !isGuestMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiUsers} className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Assessment started from: <span className="font-bold">{currentTeam.name}</span>
            </span>
          </div>
        </div>
      )}

      {!currentTeam && !isGuestMode && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-900">
              You can save these assessment results to any of your teams on the results page.
            </span>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>Section {currentSection + 1} of {sections.length}</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Section Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {sections[currentSection].title}
        </h1>
        <p className="text-lg text-gray-600">
          {sections[currentSection].description}
        </p>
      </div>

      {/* Questions */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
        <div className="space-y-8">
          {sections[currentSection].questions.map((question, questionIndex) => {
            const key = `${currentSection}-${questionIndex}`;
            const currentValue = responses[key];
            
            return (
              <div key={questionIndex} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {questionIndex + 1}. {question}
                </h3>
                <div className="grid grid-cols-5 gap-2">
                  {scaleLabels.map((label, value) => (
                    <button
                      key={value}
                      onClick={() => handleResponse(questionIndex, value + 1)}
                      className={`p-3 text-sm font-medium rounded-lg border transition-all ${
                        currentValue === value + 1
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-center">
                        <div className="font-semibold">{value + 1}</div>
                        <div className="text-xs mt-1">{label}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
            <span>Previous</span>
          </button>
          
          <button
            onClick={handleStartOver}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors border border-red-300 text-red-700 hover:bg-red-50"
          >
            <span>Start Over</span>
          </button>
        </div>
        
        <button
          onClick={handleNext}
          disabled={!isCurrentSectionComplete()}
          className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 text-white hover:bg-blue-700"
        >
          <span>{currentSection === sections.length - 1 ? 'Complete Assessment' : 'Next Section'}</span>
          <SafeIcon icon={currentSection === sections.length - 1 ? FiCheck : FiArrowRight} className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Assessment;